import React, { useContext, useEffect, useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';

import { useFormik } from 'formik';
import * as yup from 'yup';

import logoMobile from '../../assets/images/snapsop2.png';

import { SocketContext } from '../../context';
import { UiContext } from '../../context/UiContext';
import { login } from '../../helpers/globalAuth';
import { TRIAL_SIGNUP_ENABLED, USER_ROLES } from '../../constants/main';
import useCheckMobileScreen from '../../hooks/useCheckMobileScreen';
import SplitTemplate from '../../components/SplitTemplate';
import UnderlinedInput from '../../components/Inputs/UnderlinedInput';
import ConfirmButton from '../../components/Buttons/ConfirmButton';
import classes from './styles.module.scss';

const validationSchema = yup.object({
  email: yup
    .string()
    .trim()
    .email('Please enter a valid email')
    .required('This field is required'),
  password: yup.string().required('This field is required'),
});

export default function SignInPage() {
  const navigate = useNavigate();
  const { notifyError, redirectAfterLogin, setRedirectAfterLogin } =
    useContext(UiContext);
  const { socket } = useContext(SocketContext);
  const isMobile = useCheckMobileScreen();

  const signIn = async (values) => {
    try {
      const loginResponse = await login(values.email, values.password, socket);

      // Check if user is super_admin and redirect accordingly
      const isSuperAdmin = loginResponse?.role === USER_ROLES.SUPER_ADMIN;

      // Determine final redirect destination
      let finalRedirect = redirectAfterLogin;
      
      if (isSuperAdmin) {
        // For super_admin, redirect to /super-admin if:
        // - No redirectAfterLogin is set, OR
        // - redirectAfterLogin is a default page (/my-videos or /my-library)
        const isDefaultPage = ['/my-videos', '/my-library'].includes(redirectAfterLogin);
        if (!redirectAfterLogin || isDefaultPage) {
          finalRedirect = '/super-admin';
        }
        // Otherwise, respect the redirectAfterLogin (e.g., specific meeting, etc.)
      }

      navigate(finalRedirect);

      if (redirectAfterLogin) {
        setRedirectAfterLogin('');
      }
    } catch (error) {
      console.error(error);
      notifyError(error.message);
    }
  };

  const formik = useFormik({
    initialValues: {
      email: '',
      password: '',
    },
    validationSchema,
    onSubmit: signIn,
  });

  useEffect(() => {
    const shouldRedirectToDefaultPage = ['/my-videos', '/my-library'].includes(
      redirectAfterLogin
    );

    if (!redirectAfterLogin || shouldRedirectToDefaultPage) {
      setRedirectAfterLogin(isMobile ? '/my-videos' : '/my-library');
    }
  }, [isMobile, redirectAfterLogin, setRedirectAfterLogin]);

  const isRedirectToMeeting =
    /\/my-videos\/[a-f0-9]{8}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{12}/.test(
      redirectAfterLogin
    );

  const hasError = useMemo(
    () => Object.values(formik.errors).some((error) => error),
    [formik.errors]
  );
  const hasEmptyValue = useMemo(
    () => Object.values(formik.values).some((value) => !value.length),
    [formik.values]
  );

  const isSubmitDisabled = hasError || hasEmptyValue;

  const forgotPasswordLabel = useMemo(() => {
    if (isMobile) {
      return (
        <span>
          <Link to="/forgot-password">Forgot Password?</Link>
          {TRIAL_SIGNUP_ENABLED && (
            <>
              <br />
              <br />
              Don&apos;t have account?
              <br />
              <Link to="/sign-up">Start your 30-day free trial now</Link>
            </>
          )}
        </span>
      );
    }

    return (
      <span>
        Forgot your password? <br />
        <Link to="/forgot-password">Click here</Link> to recover it
        {TRIAL_SIGNUP_ENABLED && (
          <>
            <br />
            <br />
            Don&apos;t have account?
            <br />
            <Link to="/sign-up">Start your 30-day free trial now</Link>
          </>
        )}
      </span>
    );
  }, [isMobile]);

  return (
    <div className={classes.SignInPage}>
      <SplitTemplate>
        <div className={classes.logoContainer}>
          <img className={classes.logo} src={logoMobile} alt="SnapSOP" />
        </div>
        <div className={classes.content}>
          <h1>
            {isRedirectToMeeting ? 'Sign in to view your video' : 'Sign in'}
          </h1>
          <form onSubmit={formik.handleSubmit}>
            <div className={classes.inputs}>
              <UnderlinedInput
                label={
                  formik.errors.email && formik.touched.email
                    ? formik.errors.email
                    : 'Your email'
                }
                value={formik.values.email}
                errors={formik.errors.email}
                isTouched={formik.touched.email}
                type="email"
                name="email"
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
              />

              <UnderlinedInput
                label={
                  formik.errors.password && formik.touched.password
                    ? formik.errors.password
                    : 'Your password'
                }
                value={formik.values.password}
                errors={formik.errors.password}
                isTouched={formik.touched.password}
                type="password"
                name="password"
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
              />
            </div>

            <div className={classes.row}>
              <span className={classes.forgotPassword}>
                {forgotPasswordLabel}
              </span>

              <ConfirmButton
                isDisabled={isSubmitDisabled}
                withArrow
                width={isMobile ? 60 : ''}
              />
            </div>
          </form>
        </div>
      </SplitTemplate>
    </div>
  );
}
